import { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { makeStyles } from '@mui/styles'
import {
  Grid,
  MenuItem,
  FormControl,
  Typography,
  Tooltip,
  IconButton,
  LinearProgress,
  Autocomplete,
  ToggleButtonGroup,
  ToggleButton,
  FormHelperText,
  TextField,
} from '@mui/material'

import {
  CollapsedField,
  CustomInputLabel,
  CustomSelect,
  CustomTextField,
} from '../restyled-mui/CustomInputs'
import HelpIcon from '@mui/icons-material/Help'
import { setSelectedUserSetup } from '../../redux/actions/userSetups'
import Api from '../../api'
import clsx from 'clsx'
import moment from 'moment'
import momentTimezone from 'moment-timezone'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { calendarIcon } from '../../assets/fontawesome/calendarIcon'
import { PrimaryButton } from '../restyled-mui/CustomButton'
import { transformSelectedSetup } from '../../api/utils'
import { loadStateFromLocalStorage } from '../../redux/utilsLocalStorage'
import {
  setSelectedSetupForecast,
  setSelectedSetupPlant,
  setSelectedPercentile,
  setSelectedAggregationLevel,
  setSelectedAggregationValue,
} from '../../redux/actions/metrics'
import { getPreviousPath } from '../../redux/reducers'

const sitesListWidth = 350
const tabletSidebarWidth = 50

const forecasts = [
  { name: 'per_plant', label: 'Per Plant' },
  { name: 'aggregated', label: 'Aggregated' },
]

// an aggregation value arrives as { [name]: {...} }, and the name is its value
const getAggregationValueKey = aggregationValue =>
  Object.keys(aggregationValue)[0]

const useStyles = makeStyles(theme => ({
  childrenContainer: {
    marginLeft: props =>
      props.fullscreen || props.toggleable ? 0 : sitesListWidth,
    width: props =>
      props.fullscreen || props.toggleable
        ? '100%'
        : `calc(100% - ${sitesListWidth}px)`,
    display: 'flex',
    flexDirection: 'column',
  },
  formContainer: {
    backgroundColor: theme.palette.common.white,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: '15px 25px',
  },
  drawer: {
    marginLeft: props => (props.toggleable ? tabletSidebarWidth - 1 : 0),
    borderRight: `${theme.palette.menu.border} 1px solid`,
    width: props => (props.fullscreen ? '100%' : sitesListWidth),
  },
  menu: {
    display: 'flex',
    flexDirection: 'column',
    width: tabletSidebarWidth,
    zIndex: 1400,
    backgroundColor: theme.palette.menu.bg,
    borderRight: `${theme.palette.menu.border} 1px solid`,
  },
  menuItem: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2, 4),
  },
  menuItemIcon: {
    textAlign: 'center',
    minWidth: '100%',
  },
  menuIcon: {
    color: theme.palette.menu.icons,
  },
  selectedMenuIcon: {
    color: theme.palette.primary.main,
  },
  tickIcon: {
    color: theme.palette.primary.main,
    marginRight: theme.spacing(2),
  },
  customSubtitle: {
    fontSize: 14,
    color: theme.palette.primary.main,
    // marginBottom: '10px',
  },
  timeSlider: {
    marginTop: '33px',
  },
  calendarField: {
    paddingRight: 0,
  },
  endAdornmentIcon: {
    height: 21,
    width: 21,
    color: theme.palette.generalUi.icons,
  },
  checkboxText: {
    color: theme.palette.primary.main,
    marginTop: '12px',
  },
  tooltip: {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    fontSize: theme.typography.fontSize,
  },
  arrow: {
    color: theme.palette.primary.main,
  },
  helpIcon: {
    color: theme.palette.primary.main,
  },
  customText: {
    fontSize: 14,
    color: theme.palette.primary.main,
    padding: '30px 0px',
  },
  startMetricButton: {
    width: '100%',
  },
  tooltipContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: '4px',
  },
  sectionDelimiter: {
    paddingTop: '20px',
  },
  runButtonDelimiter: {
    paddingTop: '40px',
  },
  plantsDateErrorCss: {
    margin: '0px 5px',
    color: '#C63C3C',
  },
  toggleButtonPercent: {
    '&.Mui-selected': {
      backgroundColor: '#E6F1FC',
      color: '#368ED8',
      borderColor: '#A9CFF2',
    },

    '&.Mui-selected:hover': {
      backgroundColor: '#D6E9FB',
    },

    '&.Mui-selected.Mui-disabled': {
      backgroundColor: '#eee',
      color: '#888 !important',
      borderColor: '#ddd !important',
      opacity: 1,
      pointerEvents: 'none',
    },
  },
}))

const toggleButtonGroupStyles = {
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',

  '& .MuiToggleButtonGroup-grouped': {
    margin: '4px !important',
    border: '1px solid rgba(0,0,0,0.12) !important',
  },

  '& .MuiToggleButton-root': {
    flex: '1 0 calc(100% / 7 - 8px)',
    maxWidth: 'calc(100% / 7 - 8px)',
  },
}

const oneMonth = moment().add(30, 'days')
const today = moment()

const dateRange = {
  startDate: today.toDate(),
  endDate: oneMonth.toDate(),
}

// Interpret the picked wall-clock date+time as being in the user's timezone,
// then convert to a UTC ISO 8601 string ("2026-07-08T08:30:00") for the backend.
const toUtcIso = (localDateTime, timezone) =>
  momentTimezone
    .tz(moment(localDateTime).format('YYYY-MM-DDTHH:mm:ss'), timezone)
    .utc()
    .format('YYYY-MM-DDTHH:mm:ss')

const LatestForecastForm = props => {
  const classes = useStyles(props)
  const {
    selectedSetup: rawSelectedSetup,
    setSelectedUserSetup,
    userSetups,
    userPowerSetups,
    selectedSetupForecast,
    setSelectedSetupForecast,
    selectedSetupPlant,
    setSelectedSetupPlant,
    selectedPercentile,
    setSelectedPercentile,
    selectedAggregationLevel,
    setSelectedAggregationLevel,
    selectedAggregationValue,
    setSelectedAggregationValue,
    setLatestForecastParameters,
    handleNoDataContentUpdate,
    setLatestForecastData,
    disableButton,
    trackDisableStatus,
  } = props

  // Validate selectedSetup against userPowerSetups
  const selectedSetup = userPowerSetups.includes(rawSelectedSetup)
    ? rawSelectedSetup
    : null

  const buttonRefPlant = useRef(null)
  const buttonRefAggr = useRef(null)

  /* What each fetched list belongs to. Until it matches the current selection
     the list is the previous one's, and must not be auto-selected. Values are
     fetched per level, so they are keyed by setup and level together. */
  const plantsSetup = useRef(null)
  const aggregationLevelsSetup = useRef(null)
  const aggregationValuesKey = useRef(null)

  // plants
  const [plants, setPlants] = useState(null)
  const [plantLoading, setPlantLoading] = useState(false)
  const [plantsError, setPlantsError] = useState('')

  // percentiles
  const [percentiles, setPercentiles] = useState([])
  const [percentilesLoading, setPercentilesLoading] = useState(false)
  const [percentValid, setPercentValid] = useState(true)

  // aggregation levels
  const [aggregationLevels, setAggregationLevels] = useState([])
  const [aggregationLevelsLoading, setAggregationLevelsLoading] =
    useState(false)

  // aggregation values
  const [aggregationValues, setAggregationValues] = useState([])
  const [aggregationValuesLoading, setAggregationValuesLoading] =
    useState(false)

  // latest vs date selection
  const [dateMode, setDateMode] = useState('latest') // 'latest' | 'date'
  const [selectedDate, setSelectedDate] = useState(null)
  const [selectedTime, setSelectedTime] = useState(moment())

  // user's timezone from storage, used to convert the picked time to UTC
  const storedTimezone = loadStateFromLocalStorage('timezone')
  const timezone = storedTimezone !== undefined ? storedTimezone : 'Etc/UTC'

  const handleSetupListData = (_, newValue) => {
    setSelectedUserSetup(newValue)
    setSelectedSetupPlant(null)
    setSelectedAggregationLevel(null)
    // the value belongs to the previous setup's level, so it cannot survive it
    setSelectedAggregationValue('')
    setSelectedPercentile([50]) //add this to always on setup name change reset to 50
    if (newValue !== null) {
      handleNoDataContentUpdate({ heading: '', body: '' })
    } else {
      handleNoDataContentUpdate({
        heading: 'No setup selected',
        body: 'Select a setup and adjust parameters in order to start analysis',
      })
    }
  }

  const handleForecastChange = event => {
    const selectedForecastName = event.target.value
    const selectedForecastObject = forecasts.find(
      forecast => forecast.name === selectedForecastName,
    )
    setSelectedSetupForecast(selectedForecastObject)
    setSelectedSetupPlant(null)
    setSelectedAggregationLevel(null)
    // the value belongs to the previous level, so it cannot survive it
    setSelectedAggregationValue('')
    setLatestForecastData(null)
    handleNoDataContentUpdate({ heading: '', body: '' })
  }

  const handlePercentileChange = (event, newSelection) => {
    setSelectedPercentile(newSelection)
    setPercentValid(newSelection.length > 0)
    setLatestForecastData(null)
  }

  const createLatestForecastObject = () => {
    const initObj = {
      client: transformSelectedSetup(selectedSetup),
      forecast: selectedSetupForecast.name,
      percentiles: selectedPercentile,
      selected_date:
        dateMode === 'date' && selectedDate
          ? toUtcIso(
              moment(
                `${moment(selectedDate).format('YYYY-MM-DD')} ${moment(
                  selectedTime,
                ).format('HH:mm')}`,
                'YYYY-MM-DD HH:mm',
              ),
              timezone,
            )
          : // Latest = current instant in UTC (already timezone-independent)
            momentTimezone.utc().format('YYYY-MM-DDTHH:mm:ss'),
    }

    const isPerPlant = selectedSetupForecast.name === 'per_plant'

    if (isPerPlant) {
      const perPlantObj = {
        ...initObj,
        plant: selectedSetupPlant,
      }
      return perPlantObj
    }

    // aggregated branch
    const aggregationEntry = aggregationValues.find(
      item => selectedAggregationValue in item,
    )

    const aggrObj = {
      ...initObj,
      aggregation_level: selectedAggregationLevel,
      aggregation_value: aggregationEntry
        ? aggregationEntry[selectedAggregationValue]
        : undefined,
    }
    return aggrObj
  }

  const startLatestForecast = () => {
    setLatestForecastParameters(createLatestForecastObject(), dateMode)
  }

  /**
    Resets the selected plant when coming from any route except /metrics,
    so the plant selection is preserved when returning from the Metrics page.
   */
  useEffect(() => {
    if (getPreviousPath() !== '/metrics') {
      setSelectedSetupPlant(null)
    }
  }, []) // run once when component mounts

  // get setup plants
  useEffect(() => {
    const fetchPlants = async () => {
      setPlantLoading(true)
      try {
        const response = await Api.Metrics.getSetupPlants(
          selectedSetup,
          dateRange,
        )

        const startDate = moment(dateRange.startDate).format('YYYY-MM-DD')
        const endDate = moment(dateRange.endDate).format('YYYY-MM-DD')

        // until get proper response
        if (response.status === 200) {
          plantsSetup.current = selectedSetup
          if (response.data.length !== 0) {
            setPlants(response.data)
            handleNoDataContentUpdate({ heading: '', body: '' })
            setPlantsError('')
          } else {
            handleNoDataContentUpdate({
              heading: 'Info:',
              body: 'Please change the DATE SELECTION to get the active plants.',
            })
            setPlantsError(
              `No plants for selected period: ${startDate} to ${endDate}.`,
            )

            setPlants(null)
          }
        }
        setPlantLoading(false)
      } catch (error) {
        setPlants(null)
        setPlantsError('Error fetching plants for selected setup')
        console.error('Error fetching plants for selected setup:', error)
      } finally {
        setPlantLoading(false)
      }
    }
    if (selectedSetup !== null) {
      fetchPlants()
    } else {
      setPlants(null)
    }
  }, [selectedSetup])

  // get setup percentiles
  useEffect(() => {
    const fetchPercentiles = async () => {
      setPercentilesLoading(true)
      try {
        const response = await Api.Metrics.getSetupPercentiles(
          selectedSetup,
          selectedSetupForecast,
        )
        const percentiles = response.data
        const percentilesAsc = percentiles.sort((a, b) => a - b)
        setPercentiles(percentilesAsc)
        setPercentilesLoading(false)
      } catch (error) {
        setPercentiles([])
        console.error('Error fetching percentiles for selected setup:', error)
      } finally {
        setPercentilesLoading(false)
      }
    }
    if (selectedSetup !== null) {
      fetchPercentiles()
    } else {
      setPercentiles([])
    }
  }, [selectedSetup, selectedSetupForecast])

  // get setup aggregation levels
  useEffect(() => {
    const fetchAggregationLevel = async () => {
      setAggregationLevelsLoading(true)
      try {
        const response =
          await Api.Metrics.getClientAggregationLevels(selectedSetup)
        setAggregationLevels(response.data)
        aggregationLevelsSetup.current = selectedSetup
        setAggregationLevelsLoading(false)
      } catch (error) {
        setAggregationLevels([])
        aggregationLevelsSetup.current = selectedSetup
        console.error(
          'Error fetching aggregation levels for selected setup:',
          error,
        )
      } finally {
        setAggregationLevelsLoading(false)
      }
    }
    if (selectedSetup !== null) {
      fetchAggregationLevel()
    } else {
      setAggregationLevels([])
    }
  }, [selectedSetup])

  // get setup aggregation values
  useEffect(() => {
    const fetchAggregationValues = async () => {
      setAggregationValuesLoading(true)
      try {
        const response = await Api.Metrics.getClientAggregationValues(
          selectedSetup,
          selectedAggregationLevel,
        )
        setAggregationValues(response.data)
        aggregationValuesKey.current = `${selectedSetup}|${selectedAggregationLevel}`
        setAggregationValuesLoading(false)
      } catch (error) {
        setAggregationValues([])
        aggregationValuesKey.current = `${selectedSetup}|${selectedAggregationLevel}`
        console.error(
          'Error fetching aggregation level for selected setup:',
          error,
        )
      } finally {
        setAggregationValuesLoading(false)
      }
    }
    if (selectedSetup !== null && selectedAggregationLevel !== null) {
      fetchAggregationValues()
    } else {
      setAggregationValues([])
    }
  }, [selectedSetup, selectedAggregationLevel])

  /* When there is only one option the select is collapsed to plain text, so
     nothing would ever select it: do it here. The list must belong to the
     current selection, otherwise on a setup change we would select an option
     from the previous setup before its own list has arrived. */
  useEffect(() => {
    if (
      plantsSetup.current === selectedSetup &&
      plants?.length === 1 &&
      !selectedSetupPlant
    ) {
      setSelectedSetupPlant(plants[0])
    }
  }, [selectedSetup, plants, selectedSetupPlant, setSelectedSetupPlant])

  useEffect(() => {
    if (
      aggregationLevelsSetup.current === selectedSetup &&
      aggregationLevels.length === 1 &&
      !selectedAggregationLevel
    ) {
      setSelectedAggregationLevel(aggregationLevels[0])
    }
  }, [
    selectedSetup,
    aggregationLevels,
    selectedAggregationLevel,
    setSelectedAggregationLevel,
  ])

  useEffect(() => {
    const belongsToSelection =
      aggregationValuesKey.current ===
      `${selectedSetup}|${selectedAggregationLevel}`

    if (
      belongsToSelection &&
      aggregationValues.length === 1 &&
      selectedAggregationValue === ''
    ) {
      setSelectedAggregationValue(getAggregationValueKey(aggregationValues[0]))
    }
  }, [
    selectedSetup,
    selectedAggregationLevel,
    aggregationValues,
    selectedAggregationValue,
    setSelectedAggregationValue,
  ])

  const isEmptyOrContainsUndefined =
    userSetups.length === 0 || userSetups.some(x => x === undefined)

  // Only one option to pick from, so there is nothing to pick: show as text.
  const isSingleSetup = userSetups.length === 1

  // In Date mode both a valid date and a valid time must be selected before running
  const dateSelectionValid =
    dateMode !== 'date' ||
    (!!selectedDate &&
      moment(selectedDate).isValid() &&
      !!selectedTime &&
      moment(selectedTime).isValid())

  const renderSetupName = () => {
    if (isSingleSetup) {
      return (
        <Grid item md={12}>
          <CollapsedField label="Setup Name" value={userSetups[0]} />
        </Grid>
      )
    }

    return (
      <Grid item md={12}>
        <div className={classes.tooltipContainer}>
          <CustomInputLabel>Setup Name</CustomInputLabel>
          <Tooltip
            title="You can search for a value by typing in the box!"
            placement="top"
            arrow
            classes={{
              tooltip: classes.tooltip,
              arrow: classes.arrow,
            }}
          >
            <span>
              <IconButton
                style={{ padding: '0' }}
                aria-label="info"
                disableTouchRipple
              >
                <HelpIcon fontSize="small" className={classes.helpIcon} />
              </IconButton>
            </span>
          </Tooltip>
        </div>
        <FormControl fullWidth variant="outlined" size="small">
          <Autocomplete
            disabled={trackDisableStatus}
            variant="outlined"
            size="small"
            options={userSetups}
            value={selectedSetup}
            autoHighlight
            onChange={handleSetupListData}
            renderInput={params => (
              <CustomTextField
                {...params}
                inputProps={{
                  ...params.inputProps,
                  style: { padding: '1px 5px' },
                }}
              />
            )}
          />
        </FormControl>
      </Grid>
    )
  }

  const renderForecastSelection = () => {
    return (
      <Grid item md={12} className={classes.sectionDelimiter}>
        <Typography
          variant="h4"
          className={clsx(classes.formSubtitle, classes.customSubtitle)}
        >
          Forecast Type Selection
        </Typography>

        <CustomInputLabel>Forecast</CustomInputLabel>
        <FormControl
          fullWidth
          margin="dense"
          variant="outlined"
          size="small"
          disabled={trackDisableStatus}
        >
          <CustomSelect
            inputProps={{
              margin: 'dense',
            }}
            value={selectedSetupForecast.name}
            onChange={handleForecastChange}
            name={'forecastId'}
          >
            {forecasts.map(forecast => {
              return (
                <MenuItem
                  key={forecast.name}
                  value={forecast.name}
                  className={classes.selectMenuItem}
                >
                  {forecast.label}
                </MenuItem>
              )
            })}
          </CustomSelect>
        </FormControl>
      </Grid>
    )
  }

  const renderDateSelection = () => {
    return (
      <Grid item md={12} className={classes.sectionDelimiter}>
        <Typography
          variant="h4"
          className={clsx(classes.formSubtitle, classes.customSubtitle)}
          style={{ marginBottom: '10px' }}
        >
          Date Selection
        </Typography>

        <ToggleButtonGroup
          value={dateMode}
          exclusive
          size="small"
          color="primary"
          disabled={trackDisableStatus}
          sx={{ mb: dateMode === 'latest' ? '15px' : 0 }}
          onChange={(_, newMode) => {
            if (newMode !== null) {
              setDateMode(newMode)
              if (newMode === 'date' && !selectedDate) {
                setSelectedDate(moment().toDate())
                setSelectedTime(moment())
              }
              setLatestForecastData(null)
            }
          }}
          aria-label="latest-date-toggle"
        >
          <ToggleButton value="latest" className={classes.toggleButtonPercent}>
            Latest
          </ToggleButton>
          <ToggleButton value="date" className={classes.toggleButtonPercent}>
            Date
          </ToggleButton>
        </ToggleButtonGroup>

        {dateMode === 'date' && (
          <div
            style={{
              display: 'flex',
              gap: '10px',
              alignItems: 'flex-start',
              marginBottom: '15px',
            }}
          >
            <DatePicker
              maxDate={moment().toDate()}
              label="Select date"
              value={selectedDate}
              disabled={trackDisableStatus}
              PopperProps={{ placement: 'bottom-start' }}
              onChange={date => {
                setSelectedDate(date)
                setLatestForecastData(null)
              }}
              inputFormat="YYYY-MM-DD"
              components={{
                OpenPickerIcon: () => (
                  <FontAwesomeIcon
                    icon={calendarIcon}
                    className={classes.endAdornmentIcon}
                  />
                ),
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  error={!selectedDate}
                  variant="outlined"
                  margin="normal"
                  sx={{ flex: 1, minWidth: 0 }}
                />
              )}
            />
            <TimePicker
              label="Time"
              value={selectedTime}
              disabled={trackDisableStatus}
              ampm={false}
              inputFormat="HH:mm"
              disableOpenPicker
              onChange={time => {
                setSelectedTime(time)
                setLatestForecastData(null)
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  error={!selectedTime || !moment(selectedTime).isValid()}
                  variant="outlined"
                  margin="normal"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    ...params.inputProps,
                    style: { textAlign: 'center' },
                  }}
                  sx={{ width: 90, flexShrink: 0 }}
                />
              )}
            />
          </div>
        )}
      </Grid>
    )
  }

  const renderSectionHeadline = caption => {
    return (
      <Typography
        variant="h4"
        className={clsx(classes.formSubtitle, classes.customSubtitle)}
      >
        {caption}
      </Typography>
    )
  }

  const renderPlants = () => {
    return (
      <div>
        {plantLoading ? (
          <LinearProgress style={{ marginTop: '20px' }} />
        ) : plants !== null ? (
          plants.length === 1 ? (
            <CollapsedField label="Plant" value={plants[0]} />
          ) : (
            <>
              <CustomInputLabel>Plant</CustomInputLabel>
              <FormControl fullWidth variant="outlined" size="small">
                <Autocomplete
                  disabled={trackDisableStatus}
                  variant="outlined"
                  size="small"
                  options={plants}
                  value={selectedSetupPlant}
                  autoHighlight
                  onChange={(_, newPlant) => {
                    setSelectedSetupPlant(newPlant)
                    setLatestForecastData(null)
                  }}
                  renderInput={params => (
                    <CustomTextField
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        style: { padding: '1px 5px' },
                      }}
                    />
                  )}
                />
              </FormControl>
            </>
          )
        ) : (
          <CustomInputLabel style={{ color: 'orangered' }}>
            {plantsError}
          </CustomInputLabel>
        )}
      </div>
    )
  }

  const renderPercentiles = () => {
    return (
      <div>
        <CustomInputLabel style={{ marginBottom: '4px' }}>
          Percentiles
        </CustomInputLabel>
        {percentilesLoading ? (
          <LinearProgress style={{ marginTop: '20px' }} />
        ) : percentiles.length !== 0 ? (
          <>
            <ToggleButtonGroup
              value={selectedPercentile}
              onChange={handlePercentileChange}
              size="small"
              color="primary"
              disabled={trackDisableStatus}
              fullWidth
              sx={toggleButtonGroupStyles}
            >
              {percentiles.map(percentile => (
                <ToggleButton
                  key={percentile}
                  value={percentile}
                  className={classes.toggleButtonPercent}
                  disabled={trackDisableStatus}
                >
                  {percentile}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            {!percentValid && (
              <FormHelperText id="component-error-text">
                At least one selected percentile is required.
              </FormHelperText>
            )}
          </>
        ) : (
          <CustomInputLabel>
            No available percentiles for selected setup.
          </CustomInputLabel>
        )}
      </div>
    )
  }

  const renderRunBtn = buttonRef => {
    return (
      <div className={classes.runButtonDelimiter}>
        <PrimaryButton
          ref={buttonRef}
          onClick={startLatestForecast}
          style={{
            background:
              disableButton || !percentValid || !dateSelectionValid
                ? '#ccc'
                : undefined,
          }}
          className={classes.startMetricButton}
          disabled={disableButton || !percentValid || !dateSelectionValid}
        >
          RUN
        </PrimaryButton>
      </div>
    )
  }

  const renderAggregationLevel = () => {
    if (!aggregationLevelsLoading && aggregationLevels.length === 1) {
      return (
        <Grid item md={12}>
          <CollapsedField
            label="Aggregation Level"
            value={aggregationLevels[0]}
          />
        </Grid>
      )
    }

    return (
      <Grid item md={12}>
        <CustomInputLabel>Aggregation Level</CustomInputLabel>
        {aggregationLevelsLoading ? (
          <LinearProgress style={{ marginTop: '20px' }} />
        ) : aggregationLevels.length !== 0 ? (
          <FormControl fullWidth variant="outlined" size="small">
            <Autocomplete
              disabled={trackDisableStatus}
              variant="outlined"
              size="small"
              options={aggregationLevels}
              value={selectedAggregationLevel}
              autoHighlight
              onChange={(_, newAggrLevel) => {
                setSelectedAggregationLevel(newAggrLevel)
                setSelectedAggregationValue('')
                setLatestForecastData(null)
              }}
              renderInput={params => (
                <CustomTextField
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    style: { padding: '1px 5px' },
                  }}
                />
              )}
            />
          </FormControl>
        ) : (
          <CustomInputLabel>
            No aggregation levels for selected setup or error occurred.
          </CustomInputLabel>
        )}
      </Grid>
    )
  }

  const renderAggregationValue = () => {
    if (!aggregationValuesLoading && aggregationValues.length === 1) {
      return (
        <Grid item md={12}>
          <CollapsedField
            label="Aggregation Value"
            value={getAggregationValueKey(aggregationValues[0])}
          />
        </Grid>
      )
    }

    return (
      <Grid item md={12}>
        <CustomInputLabel style={{ marginBottom: '4px' }}>
          Aggregation Value
        </CustomInputLabel>
        {aggregationValuesLoading ? (
          <LinearProgress style={{ marginTop: '20px' }} />
        ) : aggregationValues.length !== 0 ? (
          <FormControl
            disabled={trackDisableStatus}
            fullWidth
            variant="outlined"
            size="small"
          >
            <CustomSelect
              inputProps={{
                margin: 'dense',
              }}
              value={selectedAggregationValue}
              onChange={e => {
                setSelectedAggregationValue(e.target.value)
                setLatestForecastData(null)
              }}
              name={'aggrValue'}
              defaultValue=""
            >
              {aggregationValues.map(obj => {
                const key = getAggregationValueKey(obj)
                return (
                  <MenuItem
                    key={key}
                    value={key}
                    className={classes.selectMenuItem}
                  >
                    {key}
                  </MenuItem>
                )
              })}
            </CustomSelect>
          </FormControl>
        ) : (
          <CustomInputLabel>
            No aggregation values for selected setup or error occurred.
          </CustomInputLabel>
        )}
      </Grid>
    )
  }

  return (
    <div className={classes.formContainer}>
      <Grid container spacing={4}>
        <Grid item md={12}>
          <Typography
            variant="h4"
            className={clsx(classes.formSubtitle, classes.customSubtitle)}
          >
            Setup selection
          </Typography>
          {!isEmptyOrContainsUndefined ? (
            renderSetupName()
          ) : (
            <CustomInputLabel>
              No available setup for selection or error occurred.
            </CustomInputLabel>
          )}

          {selectedSetup && (
            <>
              {renderForecastSelection()}

              {renderDateSelection()}

              {selectedSetupForecast.name === 'per_plant' && (
                <>
                  {renderSectionHeadline('Configuration')}

                  {renderPlants()}

                  {selectedSetupPlant && (
                    <>
                      {renderPercentiles()}

                      {!percentilesLoading &&
                        percentiles.length > 0 &&
                        renderRunBtn(buttonRefPlant)}
                    </>
                  )}
                </>
              )}

              {selectedSetupForecast.name === 'aggregated' && (
                <>
                  {renderSectionHeadline('Configuration')}

                  {renderAggregationLevel()}

                  {selectedAggregationLevel && (
                    <>
                      {renderAggregationValue()}

                      {selectedAggregationValue !== '' &&
                        aggregationValues.length > 0 && (
                          <>
                            {renderPercentiles()}

                            {!percentilesLoading &&
                              percentiles.length > 0 &&
                              renderRunBtn(buttonRefAggr)}
                          </>
                        )}
                    </>
                  )}
                </>
              )}
            </>
          )}
        </Grid>
      </Grid>
    </div>
  )
}

const mapStateToProps = state => {
  return {
    selectedSetup: state.userSetups.selectedSetup,
    userPowerSetups: state.userSetups.userPowerSetups,
    selectedSetupForecast: state.metrics.selectedForecast,
    selectedSetupPlant: state.metrics.selectedPlant,
    selectedPercentile: state.metrics.selectedPercentile,
    selectedAggregationLevel: state.metrics.selectedAggregationLevel,
    selectedAggregationValue: state.metrics.selectedAggregationValue,
  }
}

const mapDispatchToProps = {
  setSelectedUserSetup,
  setSelectedSetupForecast,
  setSelectedSetupPlant,
  setSelectedPercentile,
  setSelectedAggregationLevel,
  setSelectedAggregationValue,
}

export default connect(mapStateToProps, mapDispatchToProps)(LatestForecastForm)
